import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { LazyLoad } from '@fortress-technology-solutions/fortress-component-library/HOC';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { HttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { ToastContainer } from 'react-toastify';

import theme from 'themes/global';

import 'css/react-datetime.css';
import 'css/et-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import 'css/react-toastify.css';
import { AppContextProvider } from 'contexts/AppContext';
import { DialogProvider } from 'contexts/DialogContext';
import { InstallPromptProvider } from 'contexts/InstallPromptContext';
import { ToastProvider } from '@fortress-technology-solutions/fortress-component-library/contexts';
import Background from './Background';
import { getLocalAuth } from '../utils/auth';
import { ViewsContextProvider } from '../contexts/ViewsContext';
import useSetupWindowUtils from 'hooks/useSetupWindowUtils';
import DatadogErrorBoundary from 'components/Atoms/DatadogErrorBoundary';
import ChunkLoadErrorBoundary from 'components/Organisms/ChunkLoadErrorBoundary';
import AccessibleHomeIcon from 'icons/AccessibleHomeIcon';
import useInstallManifestSync from 'hooks/useInstallManifestSync';
import OfflineSyncManager from 'components/Organisms/OfflineSyncManager';
import ServiceWorkerUpdateManager from 'components/Organisms/ServiceWorkerUpdateManager';
import { queryClient, PERSIST_OPTIONS } from 'config/reactQueryClient';

const GuestPayment = LazyLoad(() => import('pages/GuestPayment'));
const Gateway = LazyLoad(() => import('pages/Gateway'));
const NotFound = LazyLoad(() => import('pages/NotFound'));
const VerifyToken = LazyLoad(() => import('./VerifyToken'));
const UniversalFusionAuthLogin = LazyLoad(
  () => import('./UniversalFusionAuthLogin'),
);

const GlobalStyles = createGlobalStyle`

  * {
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
  }

  html {
    background-attachment: fixed;
    background-color: #EEEEEE;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
  }

  body {
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    line-height: 18px;
    color: #4A4A4A;
  }

  /*
    ButtonGroup option buttons ship from the component library with
    white-space: nowrap, min-width: fit-content and overflow: hidden, so a long
    option label grows wider than its card and gets clipped mid-word on mobile.
    Scoped to the ButtonGroup markup (FormControl > Stack > button[aria-checked])
    because the DatePicker's month and year grids also put aria-checked on their
    buttons and those depend on fixed cell sizing. Remove once the library wraps
    by default -- see ENG-30973.
  */
  .MuiFormControl-root > .MuiStack-root > .MuiButtonBase-root[aria-checked] {
    min-width: 0;
    overflow: visible;
    overflow-wrap: break-word;
    white-space: normal;
  }
`;

const authLink = setContext((_, { headers }) => {
  const { authToken } = getLocalAuth();
  const newHeaders = {
    ...headers,
    authorization: authToken ? `Bearer ${authToken}` : null,
  };
  return {
    headers: newHeaders,
  };
});

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_PAYMENTS_API_URL,
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: authLink.concat(httpLink),
});

const InstallManifestSync = () => {
  const location = useLocation();

  useInstallManifestSync(location.pathname);

  return null;
};

function App() {
  useSetupWindowUtils();

  return (
    <DatadogErrorBoundary>
      <ToastProvider>
        <ApolloProvider client={client}>
          <ThemeProvider theme={theme}>
            <InstallPromptProvider>
              <DialogProvider>
                <Router forceRefresh={false}>
                  <GlobalStyles />
                  <InstallManifestSync />
                  <ServiceWorkerUpdateManager />
                  <PersistQueryClientProvider
                    client={queryClient}
                    persistOptions={PERSIST_OPTIONS}
                  >
                    <ReactQueryDevtools initialIsOpen={false} />
                    <OfflineSyncManager />
                    <AppContextProvider>
                      <ViewsContextProvider>
                        <ChunkLoadErrorBoundary>
                          {/*
                            The guest payment page paints its own white ground, like Contact Us:
                            it is opened by someone with no Fortress session and should not carry
                            the logged-in portal's backdrop.
                          */}
                          <Route
                            render={({ location: { pathname } }) =>
                              ['contact-us', 'guest-payment'].some((path) =>
                                pathname.includes(path),
                              ) ? undefined : (
                                <Background />
                              )
                            }
                          />
                          <Switch>
                            {/*
                              Must stay above the organization/property route. That route matches
                              any two segments, so /guest-payment/<token> would otherwise be read
                              as organization "guest-payment" and property "<token>", and the payer
                              would be bounced to a login screen for a property that does not exist.
                            */}
                            <Route
                              path="/guest-payment/:token"
                              component={GuestPayment}
                            />
                            <Route
                              path="/:organizationId/:propertyId"
                              component={Gateway}
                            />
                            <Route
                              exact
                              path="/verifyToken"
                              component={VerifyToken}
                            />
                            <Route
                              exact
                              path="/login"
                              component={UniversalFusionAuthLogin}
                            />

                            <Route component={NotFound} />
                          </Switch>
                        </ChunkLoadErrorBoundary>
                      </ViewsContextProvider>
                    </AppContextProvider>
                  </PersistQueryClientProvider>
                  <ToastContainer theme="colored" pauseOnFocusLoss={false} />
                </Router>
              </DialogProvider>
            </InstallPromptProvider>
          </ThemeProvider>
        </ApolloProvider>
      </ToastProvider>
      <AccessibleHomeIcon />
    </DatadogErrorBoundary>
  );
}

export default App;
